/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type SupportedLocale, toI18nLanguage } from '@coze-arch/i18n';
import { type Theme } from '@coze-arch/coze-design';

const SSO_LOCALE_QUERY_KEY = 'sso_locale';
const SSO_THEME_QUERY_KEY = 'sso_theme';
const SSO_LOCALE_SESSION_KEY = 'coze-sso-locale';
const STUDIO_LANGUAGE_STORAGE_KEY = 'i18next';
const STUDIO_THEME_STORAGE_KEY = 'coze-design-theme';

export interface SSOPreferences {
  locale?: SupportedLocale;
  theme?: Theme;
}

const normalizeSSOLocale = (value?: string | null): SupportedLocale | null => {
  const normalized = value?.trim().replace('_', '-').toLowerCase();
  switch (normalized) {
    case 'en':
    case 'en-us':
      return 'en-US';
    case 'zh':
    case 'zh-cn':
    case 'zh-sg':
    case 'zh-hans':
      return 'zh-CN';
    case 'zh-hk':
    case 'zh-tw':
    case 'zh-mo':
    case 'zh-hant':
      return 'zh-HK';
    default:
      return null;
  }
};

const normalizeSSOTheme = (value?: string | null): Theme | null => {
  const normalized = value?.trim().toLowerCase();
  return normalized === 'light' ||
    normalized === 'dark' ||
    normalized === 'system'
    ? normalized
    : null;
};

const safeSetStorage = (
  storageType: 'localStorage' | 'sessionStorage',
  key: string,
  value: string,
): void => {
  try {
    window[storageType].setItem(key, value);
  } catch (error) {
    // Storage may be unavailable in privacy-restricted browser contexts.
    void error;
    return;
  }
};

export const applySSOPreferencesFromURL = (): SSOPreferences => {
  if (typeof window === 'undefined') {
    return {};
  }
  const url = new URL(window.location.href);
  const hasLocale = url.searchParams.has(SSO_LOCALE_QUERY_KEY);
  const hasTheme = url.searchParams.has(SSO_THEME_QUERY_KEY);
  const locale = normalizeSSOLocale(url.searchParams.get(SSO_LOCALE_QUERY_KEY));
  const theme = normalizeSSOTheme(url.searchParams.get(SSO_THEME_QUERY_KEY));

  if (locale) {
    safeSetStorage('sessionStorage', SSO_LOCALE_SESSION_KEY, locale);
    safeSetStorage(
      'localStorage',
      STUDIO_LANGUAGE_STORAGE_KEY,
      toI18nLanguage(locale),
    );
  }
  if (theme) {
    safeSetStorage('localStorage', STUDIO_THEME_STORAGE_KEY, theme);
  }

  if (hasLocale || hasTheme) {
    url.searchParams.delete(SSO_LOCALE_QUERY_KEY);
    url.searchParams.delete(SSO_THEME_QUERY_KEY);
    const search = url.searchParams.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${search ? `?${search}` : ''}${url.hash}`,
    );
  }

  return {
    ...(locale ? { locale } : {}),
    ...(theme ? { theme } : {}),
  };
};

export const getSSOLocaleOverride = (): SupportedLocale | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const locale = normalizeSSOLocale(
      sessionStorage.getItem(SSO_LOCALE_SESSION_KEY),
    );
    const storedLanguage = localStorage.getItem(STUDIO_LANGUAGE_STORAGE_KEY);
    if (locale && storedLanguage && storedLanguage !== toI18nLanguage(locale)) {
      sessionStorage.removeItem(SSO_LOCALE_SESSION_KEY);
      return null;
    }
    return locale;
  } catch (error) {
    void error;
    return null;
  }
};
